/**
 * The four question shapes Back Roads asks, as big-button controls.
 *
 * Every control is at least 60px tall, every label is a full sentence in a
 * large face, and nothing here has a clock. The estimate uses a slider AND a
 * pair of coarse buttons, because a slider is a fine instrument and a thumb
 * on a phone is not; the ordering is tap-in-sequence rather than drag, for
 * the same reason.
 */

import { useMemo, useState } from 'react';
import type { EstimateUnit, Question } from '@geogame/data/backroads';

import type { Answer } from '../lib/backroads';
import { presentChoice, presentOrder } from '../lib/backroads';

export interface QuestionProps {
  readonly question: Question;
  /** Stable for the stop, so shuffled options stay put. */
  readonly seed: string;
  readonly onAnswer: (answer: Answer) => void;
}

const UNIT_LABEL: Record<EstimateUnit, string> = {
  years: '',
  hours: 'hours',
  miles: 'miles',
  feet: 'feet',
  days: 'days',
  mph: 'miles an hour',
};

function formatEstimate(value: number, unit: EstimateUnit): string {
  const n = unit === 'years' ? String(value) : value.toLocaleString('en-US');
  const label = UNIT_LABEL[unit];
  return label === '' ? n : `${n} ${label}`;
}

export function BackRoadsQuestion({ question, seed, onAnswer }: QuestionProps): React.JSX.Element {
  switch (question.kind) {
    case 'choice':
      return <ChoiceQuestion question={question} seed={seed} onAnswer={onAnswer} />;
    case 'truefalse':
      return <TrueFalseQuestion question={question} onAnswer={onAnswer} />;
    case 'estimate':
      return <EstimateQuestion question={question} onAnswer={onAnswer} />;
    case 'order':
      return <OrderQuestion question={question} seed={seed} onAnswer={onAnswer} />;
  }
}

function ChoiceQuestion({
  question,
  seed,
  onAnswer,
}: {
  readonly question: Extract<Question, { kind: 'choice' }>;
  readonly seed: string;
  readonly onAnswer: (answer: Answer) => void;
}): React.JSX.Element {
  const shown = useMemo(() => presentChoice(question, seed), [question, seed]);
  return (
    <div className="gg-br-options" role="group" aria-label="Answers">
      {shown.options.map((label, i) => (
        <button
          key={label}
          type="button"
          className="gg-br-option"
          onClick={() => onAnswer({ kind: 'choice', index: question.options.indexOf(label) })}
        >
          <span className="gg-br-option-letter" aria-hidden="true">
            {String.fromCharCode(65 + i)}
          </span>
          <span className="gg-br-option-label">{label}</span>
        </button>
      ))}
    </div>
  );
}

function TrueFalseQuestion({
  onAnswer,
}: {
  readonly question: Extract<Question, { kind: 'truefalse' }>;
  readonly onAnswer: (answer: Answer) => void;
}): React.JSX.Element {
  return (
    <div className="gg-br-options gg-br-options-two" role="group" aria-label="True or false">
      <button type="button" className="gg-br-option" onClick={() => onAnswer({ kind: 'truefalse', value: true })}>
        <span className="gg-br-option-label">True</span>
      </button>
      <button type="button" className="gg-br-option" onClick={() => onAnswer({ kind: 'truefalse', value: false })}>
        <span className="gg-br-option-label">False</span>
      </button>
    </div>
  );
}

function EstimateQuestion({
  question,
  onAnswer,
}: {
  readonly question: Extract<Question, { kind: 'estimate' }>;
  readonly onAnswer: (answer: Answer) => void;
}): React.JSX.Element {
  // Start in the middle so the first nudge is as likely to be up as down.
  const start = question.min + Math.round((question.max - question.min) / 2 / question.step) * question.step;
  const [value, setValue] = useState(start);
  const clamp = (v: number): number => Math.max(question.min, Math.min(question.max, v));
  // A coarse button moves a twentieth of the range, never less than one step.
  const coarse = Math.max(question.step, Math.round((question.max - question.min) / 20 / question.step) * question.step);

  return (
    <div className="gg-br-estimate">
      <div className="gg-br-estimate-value tabular-nums" aria-live="polite">
        {formatEstimate(value, question.unit)}
      </div>
      <div className="gg-br-estimate-row">
        <button
          type="button"
          className="gg-br-nudge"
          aria-label="Less"
          onClick={() => setValue((v) => clamp(v - coarse))}
        >
          −
        </button>
        <input
          className="gg-br-slider"
          type="range"
          min={question.min}
          max={question.max}
          step={question.step}
          value={value}
          aria-label="Your estimate"
          onChange={(e) => setValue(clamp(Number(e.target.value)))}
        />
        <button
          type="button"
          className="gg-br-nudge"
          aria-label="More"
          onClick={() => setValue((v) => clamp(v + coarse))}
        >
          +
        </button>
      </div>
      <button type="button" className="gg-br-btn gg-br-btn-primary" onClick={() => onAnswer({ kind: 'estimate', value })}>
        That is my guess
      </button>
    </div>
  );
}

function OrderQuestion({
  question,
  seed,
  onAnswer,
}: {
  readonly question: Extract<Question, { kind: 'order' }>;
  readonly seed: string;
  readonly onAnswer: (answer: Answer) => void;
}): React.JSX.Element {
  const shown = useMemo(() => presentOrder(question, seed), [question, seed]);
  /** Positions in `shown.items`, in the order tapped. */
  const [picked, setPicked] = useState<number[]>([]);
  const done = picked.length === shown.items.length;

  return (
    <div className="gg-br-order">
      <p className="gg-br-hint">Tap them in order, first to last.</p>
      <div className="gg-br-options" role="group" aria-label="Items to put in order">
        {shown.items.map((label, i) => {
          const rank = picked.indexOf(i);
          return (
            <button
              key={label}
              type="button"
              className="gg-br-option"
              data-picked={rank === -1 ? 'false' : 'true'}
              disabled={rank !== -1}
              onClick={() => setPicked((p) => [...p, i])}
            >
              <span className="gg-br-option-letter" aria-hidden="true">
                {rank === -1 ? '' : String(rank + 1)}
              </span>
              <span className="gg-br-option-label">{label}</span>
            </button>
          );
        })}
      </div>
      <div className="gg-br-row">
        <button type="button" className="gg-br-btn" disabled={picked.length === 0} onClick={() => setPicked([])}>
          Start over
        </button>
        <button
          type="button"
          className="gg-br-btn gg-br-btn-primary"
          disabled={!done}
          onClick={() => {
            // Map the tapped positions back to source indices: order[k] is
            // the source index the player put at position k.
            const order = picked.map((pos) => shown.perm[pos] as number);
            onAnswer({ kind: 'order', order });
          }}
        >
          Lock it in
        </button>
      </div>
    </div>
  );
}
